import { useReducer, useEffect, useCallback, useRef } from 'react';
import { ExpressionGroupsState } from '../../../../../shared/components/AdvancedExpressionBuilder/types';
import { LutienApi } from '@services';
import { exportData } from '../services/dataExport.api';
import { 
  getWorkflowsByOrganization, 
  getCustomFieldsByOrganization,
  getFieldCatalogForExport,
  getResourceValuesForExport,
  prefixFieldIdentifiers, 
  downloadBlobAsFile, 
  validateExportRequirements, 
  validateOrganizationSelected,
  getErrorMessage,
  isAbortError
} from '../utils';
import {
  ExportPayload, 
  LoadingState, 
  DataExportState,
  BackendCondition,
  DataExportAction
} from '../types/dataExport.types';
import { buildConditionDefinition } from '../../../../../shared/components/AdvancedExpressionBuilder/utils/buildConditionDefinition';

const initialLoadingState: LoadingState = {
  isLoadingOrganizations: false,
  isLoadingWorkflows: false,
  isLoadingCustomFields: false,
  isExporting: false,
  isFetchingFields: false,
};

const initialState: DataExportState = {
  organizations: [],
  workflows: [],
  customFields: [],
  selectedOrganizationId: null,
  selectedWorkflowId: null,
  selectedNativeFields: [],
  selectedCustomFields: [],
  sortAttribute: '',
  sortDirection: 'ASC' as const,
  conditions: null,
  loadingState: initialLoadingState,
  error: null,
  success: null,
};

const dataExportReducer = (state: DataExportState, action: DataExportAction): DataExportState => {
  switch (action.type) {
    case 'SET_ORGANIZATIONS':
      return { ...state, organizations: action.payload };
    
    case 'SET_WORKFLOWS':
      return { 
        ...state, 
        workflows: action.payload,
        selectedWorkflowId: null,
        selectedNativeFields: [],
        selectedCustomFields: [],
      };
    
    case 'SET_CUSTOM_FIELDS':
      return { 
        ...state, 
        customFields: action.payload,
        selectedCustomFields: [],
      };
    
    case 'SET_ORGANIZATION':
      return { 
        ...state, 
        selectedOrganizationId: action.payload,
        selectedWorkflowId: null,
        selectedNativeFields: [],
        selectedCustomFields: [],
        workflows: [],
        customFields: [],
      };
    
    case 'SET_WORKFLOW':
      return { 
        ...state, 
        selectedWorkflowId: action.payload,
        selectedNativeFields: [],
        selectedCustomFields: [],
      };
    
    case 'SET_NATIVE_FIELDS':
      return { ...state, selectedNativeFields: action.payload };
    
    case 'SET_CUSTOM_FIELDS_SELECTION':
      return { ...state, selectedCustomFields: action.payload };
    
    case 'SET_SORT':
      return { 
        ...state, 
        sortAttribute: action.payload.attribute,
        sortDirection: action.payload.direction 
      };
    
    case 'SET_CONDITIONS':
      return { ...state, conditions: action.payload };
    
    case 'UPDATE_LOADING_STATE':
      return { 
        ...state, 
        loadingState: { ...state.loadingState, ...action.payload } 
      };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_SUCCESS':
      return { ...state, success: action.payload };
    
    case 'RESET_SELECTIONS':
      return { 
        ...state, 
        selectedWorkflowId: null,
        selectedNativeFields: [],
        selectedCustomFields: [],
      };
    
    case 'RESET_ERROR_SUCCESS':
      return { ...state, error: null, success: null };
    
    default:
      return state;
  }
};

export const useDataExport = () => {
  const [state, dispatch] = useReducer(dataExportReducer, initialState);
  
  const workflowsAbortControllerRef = useRef<AbortController | null>(null);
  const customFieldsAbortControllerRef = useRef<AbortController | null>(null);

  const fetchOrganizations = useCallback(async () => {
    dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isLoadingOrganizations: true } });
    try {
      const organizations = await LutienApi.getOrganizations();
      dispatch({ type: 'SET_ORGANIZATIONS', payload: organizations });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load organizations' });
    } finally {
      dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isLoadingOrganizations: false } });
    }
  }, []);

  const fetchWorkflows = useCallback(async (organizationId: string) => {
    if (workflowsAbortControllerRef.current) {
      workflowsAbortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    workflowsAbortControllerRef.current = abortController;
    
    dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isLoadingWorkflows: true } });
    try {
      const workflows = await getWorkflowsByOrganization(organizationId, abortController.signal);
      
      if (!abortController.signal.aborted) {
        dispatch({ type: 'SET_WORKFLOWS', payload: workflows });
      }
    } catch (error: unknown) {
      if (!isAbortError(error)) {
        if (!abortController.signal.aborted) {
          dispatch({ type: 'SET_ERROR', payload: 'Failed to load workflows' });
          dispatch({ type: 'SET_WORKFLOWS', payload: [] });
        }
      }
    } finally {
      if (!abortController.signal.aborted) {
        dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isLoadingWorkflows: false } });
      }
      if (workflowsAbortControllerRef.current === abortController) {
        workflowsAbortControllerRef.current = null;
      }
    }
  }, []);

  const fetchCustomFields = useCallback(async (organizationId: string) => {
    if (customFieldsAbortControllerRef.current) {
      customFieldsAbortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    customFieldsAbortControllerRef.current = abortController;
    
    dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isLoadingCustomFields: true } });
    try {
      const customFields = await getCustomFieldsByOrganization(organizationId, abortController.signal);
      
      if (!abortController.signal.aborted) {
        dispatch({ type: 'SET_CUSTOM_FIELDS', payload: customFields });
      }
    } catch (error: unknown) {
      if (!isAbortError(error)) {
        if (!abortController.signal.aborted) {
          dispatch({ type: 'SET_ERROR', payload: 'Failed to load custom fields' });
          dispatch({ type: 'SET_CUSTOM_FIELDS', payload: [] });
        }
      }
    } finally {
      if (!abortController.signal.aborted) {
        dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isLoadingCustomFields: false } });
      }
      if (customFieldsAbortControllerRef.current === abortController) {
        customFieldsAbortControllerRef.current = null;
      }
    }
  }, []);

  const handleOrganizationChange = useCallback((organizationId: string) => {
    dispatch({ type: 'SET_ORGANIZATION', payload: organizationId });

    if (organizationId) {
      fetchWorkflows(organizationId);
      fetchCustomFields(organizationId);
    }
  }, [fetchWorkflows, fetchCustomFields]);

  const handleWorkflowChange = useCallback((workflowId: string) => {
    dispatch({ type: 'SET_WORKFLOW', payload: workflowId });
  }, []);

  const handleNativeFieldsChange = useCallback((fieldIdentifiers: string[]) => {
    dispatch({ type: 'SET_NATIVE_FIELDS', payload: fieldIdentifiers });
  }, []);

  const handleCustomFieldsChange = useCallback((fieldIdentifiers: string[]) => {
    dispatch({ type: 'SET_CUSTOM_FIELDS_SELECTION', payload: fieldIdentifiers });
  }, []);

  const handleSortChange = useCallback((attribute: string, direction: 'ASC' | 'DESC') => {
    dispatch({ type: 'SET_SORT', payload: { attribute, direction } });
  }, []);

  const handleConditionsChange = useCallback((conditions: ExpressionGroupsState) => {
    dispatch({ type: 'SET_CONDITIONS', payload: conditions });
  }, []);

  const fetchFieldCatalog = useCallback(async (searchTerm?: string) => {
    if (!validateOrganizationSelected(state.selectedOrganizationId)) {
      return [];
    }

    dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isFetchingFields: true } });
    try {
      const fields = await getFieldCatalogForExport(state.selectedOrganizationId!, searchTerm);
      const fieldsWithPrefixes = prefixFieldIdentifiers(fields);
      return fieldsWithPrefixes;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load field catalog' });
      return [];
    } finally {
      dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isFetchingFields: false } });
    }
  }, [state.selectedOrganizationId]);

  const fetchResourceValues = useCallback(async (resourceId: string, searchTerm?: string) => {
    if (!validateOrganizationSelected(state.selectedOrganizationId)) {
      console.warn('No organization selected for resource values fetch');
      return [];
    }
    console.log('fetchResourceValues', resourceId, state.selectedOrganizationId, searchTerm);
    return await getResourceValuesForExport(resourceId as any, state.selectedOrganizationId!, searchTerm);
  }, [state.selectedOrganizationId]);

  const getConditionsAsJson = useCallback((): BackendCondition[] | undefined => {
    if (!state.conditions) return undefined;
    const condition = buildConditionDefinition(state.conditions);
    return condition ? [condition] : undefined;
  }, [state.conditions]);

  const handleSubmit = useCallback(async () => {
    validateExportRequirements(state);

    dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isExporting: true } });
    dispatch({ type: 'RESET_ERROR_SUCCESS' });
    
    try {
      const payload: ExportPayload = {
        workflowId: state.selectedWorkflowId!,
        nativeFieldIdentifiers: state.selectedNativeFields,
        customFieldIdentifiers: state.selectedCustomFields,
        sortAttribute: state.sortAttribute,
        sortDirection: state.sortDirection,
        conditions: getConditionsAsJson(),
      };

      const blob = await exportData(payload);
      
      downloadBlobAsFile(blob);

      dispatch({ type: 'SET_SUCCESS', payload: 'Export completed successfully! File downloaded automatically.' });
      return true;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'UPDATE_LOADING_STATE', payload: { isExporting: false } });
    }
  }, [state, getConditionsAsJson]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  useEffect(() => {
    return () => {
      if (workflowsAbortControllerRef.current) {
        workflowsAbortControllerRef.current.abort();
      }
      if (customFieldsAbortControllerRef.current) {
        customFieldsAbortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    ...state,
    handleOrganizationChange,
    handleWorkflowChange,
    handleNativeFieldsChange,
    handleCustomFieldsChange,
    handleSortChange,
    handleConditionsChange,
    handleSubmit,
    fetchFieldCatalog,
    fetchResourceValues,
  };
};
